'use client';

import { useEffect } from 'react';

/**
 * Microsoft Clarity session-replay loader. Client-only.
 *
 * Gated the SAME way as Firebase Analytics (see `shared/lib/firebase.ts`):
 * only in `NEXT_PUBLIC_APP_ENV === 'production'`, so staging / local / `hybrid`
 * builds never pollute the Clarity project.
 *
 * The project id is public by design (it lives in the client snippet on every
 * Clarity-instrumented site — not a secret), so it is hardcoded as a default
 * here, exactly like the Firebase GA4 measurement id in `firebase.ts`. The
 * `NEXT_PUBLIC_CLARITY_PROJECT_ID` env var can still override it (e.g. a
 * separate staging Clarity project) without a code change.
 *
 * NOTE on the `is_test` opt-out: Firebase routes the server `is_test` flag
 * through `setAnalyticsCollection()` at profile load. Clarity has no equivalent
 * lightweight per-session collection toggle at this mount point (the profile
 * isn't guaranteed loaded here), and wiring one would require deep plumbing.
 * We therefore gate on env + production only for now; internal test traffic in
 * production is a small, acceptable amount of replay noise. If it matters later,
 * call `window.clarity('stop')` from the profile loader when `is_test` is true.
 */
export function ClarityAnalytics() {
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') return;

    // Public tracking id (see file header). Env var overrides the default.
    const projectId =
      process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID || 'xfkb3yxrqd';
    if (!projectId) return;

    // Guard against double-injection (React strict-mode double effect, remounts).
    if (document.getElementById('ms-clarity-script')) return;

    // Official clarity.ms snippet (inlined; no external tag manager).
    const script = document.createElement('script');
    script.id = 'ms-clarity-script';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = `(function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");`;
    document.head.appendChild(script);
  }, []);

  return null;
}
